// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import loading from '../../../../../share/components/vue/ProcessingIcon';
import apiService from '../../../../../share/util/api/service';
import {
  executePolling,
} from '../../../../../share/util/api/polling';
import { apiErrorDefaultAction } from "../../../../../share/util/api/errorAction";
import logWrapper from '../../../../../share/logWrapper';
import { OBD_VEHICLE } from '../../../../../constants/usageCountTypes';
import memoPage from '../../../../vue/MemoPage.vue';
import { pushFunctionList } from "../../../../../router/obd";
import { executeSyncRequest } from '../../../../../share/util/api/syncRequest';
import diagnoseResource from "../../../../../share/util/diagnose";

const i18nVehicleInfoKey = 'vehicle_info_name';
const i18nInfoTypeKey = 'info_type';
const i18nUnitKey = 'obd_unit';

export default {
  name: "obdGetVehicleInfo",
  data() {
    return {
      processing: false,
      measureId: "",
      resData: null,
      showData: null,
      modalMemo: false,
      showErrorVehicleModal: false,
      isMemoDialogLoading: false,
    };
  },

  props: {
    destinationAPI: { type: String, required: false, default: apiService.OBD },
  },

  watch: {
    language() {
      this.parseShowData(this.resData);
    }
  },

  components: {
    'processing-view': loading,
    'memo-Page': memoPage,
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
      obdDataId: state => state.obd.obdDataId,
      projectId: state => state.vehicle.vehicleInfo ? state.vehicle.vehicleInfo.vehicle.project.id : null,
      systemNo: state => state.obd.menuSupport?.systemNo,
      language: state => state.systems.language,
    }),

    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断/OBDシステムのうちどの機能なのか判別する
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    }
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),
    ...mapActions('observation', ['usageCountUp']),

    // 車両情報取得開始
    async getVehicleInfoExecute() {
      try {
        this.processing = true;

        // OBDシステム利用回数カウントアップ
        this.usageCountUp({ type: OBD_VEHICLE });
        const response = await executePolling(
          this,
          // 呼び出したい POST API を実装
          async () =>
            await apiService.postCall(apiService.OBD, `/netApps/${this.netAppId}/obd/${this.obdDataId}/systems/${this.$route.params.systemNo}/getVehicleInfo`),
          // 呼び出したい GET API を実装
          async (postApiResponse) => { return await apiService.getCall(apiService.OBD, `/netApps/${this.netAppId}/requests/${postApiResponse?.data?.request_id}`, this.operationScreen); },
          // エラー発生のコールバック
          (errorType) => { errorType; this.transitionUnExecutableScreen(); },
          // POST API レスポンスチェックのコールバックはデフォルト処理を使用
          undefined,
          // GET API レスポンスチェックのコールバックはデフォルト処理を使用
          undefined,
          (result) => { this.validateErrorResult(result); },
          (result) => { this.validateErrorResult(result); }
        );
        if (response) {
          // 計測データIDを取得
          this.measureId = response.data.measure_id;
          // 車両情報データ取得
          await this.getGviList();
        }
      } catch (error) {
        await this.transitionUnExecutableScreen();
      } finally {
        this.processing = false;
      }
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.transitionUnExecutableScreen(); },
      );
    },

    /**
     * 実行不可画面に遷移
     */
    async transitionUnExecutableScreen() {
      this.showErrorVehicleModal = true;
    },

    /**
     * 画面を閉じて、OBD個別選択画面に戻る
     */
    async closeWizard() {
      this.$emit("close-wizard");
      pushFunctionList(this, this.systemNo);
    },

    /**
     * 表示する形に変換・取得
     * @param {object[]} dispList バックエンドからの変換値
     */
    parseShowData(dispList) {
      // 表示データを取得
      const dispData = dispList
        .map(item => {
          let obj = null;
          // "00","20" でも "F800","F820" でもない(20の倍数)場合のみ obj を作成
          const vehicle_info = parseInt(item.vehicle_info, 16);
          if (vehicle_info % 32 !== 0) {
            obj = {
              vehicle_info: "＜" + this.$t(`${i18nVehicleInfoKey}.${item.vehicle_info}`) + "＞",
              body: item.body.map(bodyItem => ({
                item_name: this.$t(`${i18nInfoTypeKey}.${bodyItem.item_name}`),
                value: bodyItem.value === null ? '' : bodyItem.value,
                unit: bodyItem.unit === '' ? '' : this.$t(`${i18nUnitKey}.${bodyItem.unit}`),
              }))
            };
          }
          return obj;
        })
        // null を取り除く
        .filter(item => item !== null);
      this.showData = dispData;
    },

    /**
     * 車両情報データ取得
     */
    async getGviList() {
      await executeSyncRequest(
        this,
        async () => {
          return await apiService.getCall(apiService.OBD, `/obd/${this.obdDataId}/measures/${this.measureId}/vehicleInfoList`, this.operationScreen);
        },
        // エラー発生のコールバックを実装する。
        (errorType) => {
          logWrapper.log("errorCallback called.", errorType, true);
          this.transitionUnExecutableScreen();
        },
        // API で正常レスポンスが返ってきた際の内容チェックを実装する。
        (apiResponse) => {
          // 変換された値を置いておく
          this.resData = apiResponse.data.dispList;
          // 表示データを取得
          this.parseShowData(apiResponse.data.dispList);
        },
        // API 結果が異常の場合
        (result) => { this.validateErrorResult(result); },
      );
    },

    /**
     * メモダイアログを表示
     */
    openNoteModal() {
      this.modalMemo = true;
    },

    /**
     * メモダイアログを閉じる
     */
    closeNoteModal() {
      this.modalMemo = false;
    },

    /**
     * メモ情報の保存
     */
    async memoSave() {
      await this.$refs.memo.save();
    },

    /**
    * メモ情報のクリア
    */
    memoClear: function () {
      this.$refs.memo.clear();
    },
  },

  async mounted() {
    //実行開始
    await this.getVehicleInfoExecute();
  },

  beforeDestroy() {
  },
};